import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeRewardModal } from 'dispatcher/modalDispatcher';
import Dialog from 'seedly-component-library/dialog';
import { Message } from 'seedly-component-library';
import isEmpty from 'lodash/isEmpty';
import { RootState } from 'reducer';
import {
  TierMap,
  RewardFilter,
  RewardVoucher,
} from 'entity/reward/rewardTypes';
import { checkIsRedeemable, mapTagType } from 'entity/reward/rewardUtils';
import dayjs from 'dayjs';
import clsx from 'clsx';

import { Copy } from 'react-feather';
import rewardApi from 'entity/reward/rewardApi';
import LabelTag from '../RewardItem/LabelTag';
import RewardMarkdown from './RewardMarkdown';
import useBottomSectionContent from './hooks/useBottomSectionContent';
import ConfirmClaimRewardModal from '../ConfirmClaimRewardModal';
import RedeemRewardSuccessModal from '../RedeemRewardSuccessModal';
import RedeemRewardFailureModal from '../RedeemRewardFailureModal';

const RewardDetailModal = (props: {
  setSelectedTab: (param: RewardFilter) => void;
}) => {
  const { setSelectedTab } = props;
  const dispatch = useDispatch();

  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [voucherDetail, setVoucherDetail] = useState<RewardVoucher>(null);

  const rewardById = useSelector((state: RootState) => state.reward.byId);
  const profile = useSelector((state: RootState) => state.profile.user);
  const isRewardModalOpen = useSelector(
    (state: RootState) => state.modal.isRewardModalOpen,
  );
  const rewardId = useSelector(
    (state: RootState) => state.modal.rewardModalMeta.rewardId,
  );
  const claimId = useSelector(
    (state: RootState) => state.modal.rewardModalMeta.claimId,
  );

  const reward = rewardById[rewardId];
  const claim = reward?.claims.find(claimItem => {
    return claimItem.id === claimId;
  });
  const redeemable = checkIsRedeemable(profile, reward);
  const tagType = mapTagType(reward, redeemable, claim);
  useEffect(() => {
    if (rewardId && claimId) {
      rewardApi.getVoucherDetail(rewardId, claimId).then(res => {
        if (res.ok) {
          setVoucherDetail(res.data.data);
        }
      });
    }
  }, [rewardId, claimId]);

  const BottomSectionContent = useBottomSectionContent({
    reward,
    setOpenConfirmationModal: setOpenConfirmation,
    claim,
  });

  if (!reward) return null;

  return (
    <Dialog
      isOpen={isEmpty(reward) ? false : isRewardModalOpen}
      onCloseClick={() => {
        dispatch(closeRewardModal());
      }}
      headerTitle="Redeem Reward"
      contentClassName="p-0 flex flex-col"
      headerClassName="p-4"
    >
      <div className="overflow-y-auto lg:max-h-[60vh]">
        <div className="w-full relative">
          <img
            className={clsx(
              'border-b border-neutral-400 object-cover w-full',
              (tagType === 'fully_redeemed' ||
                tagType === 'lock' ||
                tagType === 'used') &&
                'opacity-50',
            )}
            alt="banner"
            src={reward.banner}
          />
          <div className="absolute top-3 right-3">
            <LabelTag tagType={tagType} />
          </div>
        </div>
        <div className="flex flex-col gap-3 w-full px-4 mt-4">
          <p className="text-base font-bold">{reward.title}</p>
          {!claim ? (
            <div className="flex items-center gap-10 lg:gap-20">
              <div className="flex flex-col gap-1">
                <p className="text-xs font-bold text-neutral-500">
                  TIER REQUIREMENT
                </p>
                <p> {TierMap[reward.minTier || 0]}</p>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-xs font-bold text-neutral-500">
                  CREDIT REQUIREMENT
                </p>
                <p>{reward.minCredits?.toLocaleString()} Credits</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-10 lg:gap-20">
              {claim.reward_type === 'text' ? (
                <>
                  <div className="flex flex-col gap-1">
                    <p className="text-xs font-bold text-neutral-500">
                      VOUCHER CODE
                    </p>
                    <div className="flex items-center gap-2">
                      <p>{claim.content}</p>
                      <Copy
                        onClick={() => {
                          if (window.isSecureContext) {
                            navigator.clipboard.writeText(claim.content);
                            Message.success('Copied code');
                          }
                        }}
                        className="cursor-pointer"
                        size={20}
                      />
                    </div>
                  </div>
                  {voucherDetail?.pin && (
                    <div className="flex flex-col gap-1">
                      <p className="text-xs font-bold text-neutral-500">
                        VOUCHER PIN
                      </p>
                      <p>{voucherDetail?.pin}</p>
                    </div>
                  )}
                  {voucherDetail && (
                    <div className="flex flex-col gap-1">
                      <p className="text-xs font-bold text-neutral-500">
                        VALIDITY
                      </p>
                      <div className="flex items-center gap-2">
                        <p>
                          Valid till{' '}
                          {dayjs(voucherDetail.expiresAt).format('DD MMM YYYY')}
                        </p>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="flex flex-col gap-1">
                    <p className="text-xs font-bold text-neutral-500">
                      REDEMPTION DATE
                    </p>
                    <p>{dayjs(claim.created_at).format('DD MMM YYYY')}</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <p className="text-xs font-bold text-neutral-500">
                      REDEMPTION ID
                    </p>
                    <p>{claim.id}</p>
                  </div>
                </>
              )}
            </div>
          )}
          <div className="horizontal-divider" />
          <div className="flex flex-col gap-4 w-full">
            {reward.instructions && claim && (
              <div className="flex flex-col gap-1">
                <p className="text-sm font-bold">How To Use</p>
                <RewardMarkdown>{reward.instructions}</RewardMarkdown>
              </div>
            )}
            <div className="flex flex-col gap-1">
              <p className="text-sm font-bold">Description</p>
              <RewardMarkdown>{reward.description}</RewardMarkdown>
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-sm font-bold">Terms & Conditions</p>
              <RewardMarkdown>{reward.termsAndConditions}</RewardMarkdown>
            </div>
          </div>
        </div>
      </div>
      <div className="sticky bottom-0 mt-6 p-4 w-100 bg-white rounded-b-2xl shadow-rewardFooter">
        {BottomSectionContent}
      </div>
      <ConfirmClaimRewardModal
        setOpenConfirmationModal={setOpenConfirmation}
        isOpen={openConfirmation}
        reward={reward}
      />
      <RedeemRewardSuccessModal
        setSelectedTab={setSelectedTab}
        reward={reward}
      />
      <RedeemRewardFailureModal />
    </Dialog>
  );
};

export default RewardDetailModal;
